import { AxiosInstance, AxiosResponse } from 'axios'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

import {
  BulkViewStatsResponse,
  HealthCheckResponse,
  QueueStatsResponse,
  TrackViewRequest,
  ViewCountApiResponse,
  ViewCountEntity,
  ViewCountEntityType,
  ViewDashboardPagedParams,
  ViewDashboardPagedResponse,
  ViewDashboardStatsResponse,
  ViewStatsResponse,
} from '@/types/view-count.type'

export class ViewCountService {
  private static instance: ViewCountService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): ViewCountService {
    if (!ViewCountService.instance) {
      ViewCountService.instance = new ViewCountService(baseService)
    }
    return ViewCountService.instance
  }

  /**
   * Track a view for a specific entity
   */
  async trackView(options: TrackViewRequest): Promise<ViewCountApiResponse<{ success: boolean }>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<{ success: boolean }>> = await this.api.post(
        '/view-count/track',
        options
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to track view')
    }
  }

  /**
   * Get view statistics for a specific entity
   */
  async getViewStats(
    entityType: ViewCountEntityType,
    entityId: string,
    windowHours?: number
  ): Promise<ViewCountApiResponse<ViewStatsResponse>> {
    try {
      const params = new URLSearchParams({
        entityType,
        entityId,
        ...(windowHours && { windowHours: windowHours.toString() }),
      })

      const response: AxiosResponse<ViewCountApiResponse<ViewStatsResponse>> = await this.api.get(
        `/view-count/stats?${params}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to get view statistics')
    }
  }

  /**
   * Get bulk view statistics for multiple entities
   */
  async getBulkViewStats(
    entities: ViewCountEntity[],
    windowHours?: number
  ): Promise<ViewCountApiResponse<BulkViewStatsResponse>> {
    try {
      const params = new URLSearchParams({
        entities: JSON.stringify(entities),
        ...(windowHours && { windowHours: windowHours.toString() }),
      })

      const response: AxiosResponse<ViewCountApiResponse<BulkViewStatsResponse>> = await this.api.get(
        `/view-count/stats/bulk?${params}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to get bulk view statistics')
    }
  }

  /**
   * Health check for the view count service
   */
  async healthCheck(): Promise<ViewCountApiResponse<HealthCheckResponse>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<HealthCheckResponse>> =
        await this.api.get('/view-count/health')
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to perform health check')
    }
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<ViewCountApiResponse<QueueStatsResponse>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<QueueStatsResponse>> =
        await this.api.get('/view-count/queue/stats')
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to get queue statistics')
    }
  }

  /**
   * Clear the view count queue
   */
  async clearQueue(): Promise<ViewCountApiResponse<{ success: boolean }>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<{ success: boolean }>> =
        await this.api.post('/view-count/queue/clear')
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to clear queue')
    }
  }

  async triggerSync(
    entityType: ViewCountEntityType,
    entityId: string
  ): Promise<ViewCountApiResponse<{ success: boolean }>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<{ success: boolean }>> = await this.api.post(
        '/view-count/sync',
        { entityType, entityId }
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to trigger sync')
    }
  }

  async triggerSyncAll(): Promise<ViewCountApiResponse<{ success: boolean }>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<{ success: boolean }>> =
        await this.api.post('/view-count/sync')
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to trigger full sync')
    }
  }

  async getDashboardStats(limit = 20): Promise<ViewCountApiResponse<ViewDashboardStatsResponse>> {
    try {
      const response: AxiosResponse<ViewCountApiResponse<ViewDashboardStatsResponse>> = await this.api.get(
        `/dashboard/view-count?limit=${limit}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch view-count dashboard stats')
    }
  }

  async getDashboardPaged(
    params: ViewDashboardPagedParams
  ): Promise<ViewCountApiResponse<ViewDashboardPagedResponse>> {
    try {
      const search = new URLSearchParams({
        type: params.type,
        page: String(params.page ?? 1),
        pageSize: String(params.pageSize ?? 10),
      })
      const response: AxiosResponse<ViewCountApiResponse<ViewDashboardPagedResponse>> = await this.api.get(
        `/dashboard/view-count?${search.toString()}`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch paginated view-count dashboard stats')
    }
  }

  /**
   * Helper method to handle errors consistently
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleError(error: any, defaultMessage: string): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message)
    }
    if (error.message) {
      return new Error(error.message)
    }
    return new Error(defaultMessage)
  }
}

const viewCountService = ViewCountService.getInstance(baseService)

export default viewCountService
